import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { ContactLink, AssessmentButton, GhostLink } from "@/components/Ctas";
import { CALENDLY_URL, ASSESSMENT_URL } from "@/config";

const FOUR_AREAS = [
  { number: "01", name: "STRATEGY", tagline: "Find Direction" },
  { number: "02", name: "PROGRAMS & PRODUCTS", tagline: "Make the Work Work" },
  { number: "03", name: "SYSTEMS & CAPACITY", tagline: "Build Capacity" },
  { number: "04", name: "AI & AUTOMATION", tagline: "Create Leverage" },
];

const FORMATS = [
  {
    name: "Workshops",
    note: "Structured, facilitated sessions focused on learning, alignment, decision-making or working through a specific challenge.",
    options: ["90-minute", "Half-day", "Full-day"],
    cta: "Discuss a Workshop",
    to: "/contact?type=workshop",
    testid: "format-workshops",
  },
  {
    name: "Strategy Sprints",
    note: "Focused consulting engagements for deeper strategic work — with time for assessment, prioritization, working sessions and a defined outcome.",
    options: ["1-week", "2-week", "4-week", "Custom"],
    cta: "Discuss a Strategy Sprint",
    to: "/contact?type=strategy-sprint",
    testid: "format-sprints",
  },
];

const WORKSHOPS = [
  {
    area: "Strategy",
    name: "Strategic Planning",
    addresses: "Turning vision and goals into a clear, shared set of priorities.",
    who: "Leadership teams, boards and founders setting direction.",
    leave: "A focused set of priorities and a direction everyone understands.",
    testid: "workshop-strategic-planning",
  },
  {
    area: "Strategy",
    name: "From Strategy to Execution",
    addresses: "The gap between what's on the plan and what happens day to day.",
    who: "Teams responsible for delivering on strategic plans.",
    leave: "A practical path from goals to actions, owners and operating rhythms.",
    testid: "workshop-strategy-to-execution",
  },
  {
    area: "Programs & Products",
    name: "Program Management in Practice",
    addresses: "Planning, organizing and delivering programs with more consistency.",
    who: "Program managers, coordinators, operations leads and nonprofit teams.",
    leave: "Simple tools and rhythms for managing programs with confidence.",
    testid: "workshop-program-management",
  },
  {
    area: "Systems & Capacity",
    name: "Leadership & Delegation",
    addresses: "Too much depending on too few people.",
    who: "Founders, managers and emerging leaders.",
    leave: "Clearer roles, stronger delegation and better decision-making habits.",
    testid: "workshop-leadership-delegation",
  },
  {
    area: "Systems & Capacity",
    name: "Continuous Improvement",
    addresses: "Turning learning and feedback into better ways of working.",
    who: "Teams that want to keep improving how they work.",
    leave: "A simple improvement loop the team can run on its own.",
    testid: "workshop-continuous-improvement",
  },
  {
    area: "AI & Automation",
    name: "Practical AI",
    addresses: "Where AI and automation realistically help — and where they don't.",
    who: "Teams curious about AI but unsure where to start.",
    leave: "A shortlist of practical, responsible AI opportunities for your work.",
    testid: "workshop-practical-ai",
  },
];

const pillCls =
  "group inline-flex items-center gap-2.5 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold tracking-wide text-paper transition-colors duration-300 hover:bg-amber";

export default function Workshops() {
  useEffect(() => {
    document.title = "Workshops & Facilitation — KP Strategy";
    return () => {
      document.title =
        "KP Strategy — Design your business like a product. Run it like a system. Scale it with AI.";
    };
  }, []);

  return (
    <main data-testid="workshops-page">
      {/* Hero */}
      <section className="pt-[72px]" data-testid="workshops-hero">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 sm:pt-24 lg:px-8 lg:pb-20 lg:pt-28">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.3em] text-amber">
              <span className="inline-block h-px w-10 bg-amber" aria-hidden="true" />
              Workshops &amp; Facilitation
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-7 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl">
              From figuring out what matters to building the capability to act
              on it.
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-body sm:text-lg">
              Two ways to work together: a focused Strategy Sprint when you
              need direction, and facilitated workshops when your team is ready
              to build capability.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <a
                data-testid="workshops-cta-starting-point"
                href={ASSESSMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={pillCls}
              >
                Find the Right Starting Point
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>
              <ContactLink testid="workshops-cta-get-in-touch" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Two ways to work together */}
      <section id="two-paths" className="scroll-mt-20 border-t border-line py-20 sm:py-24" data-testid="two-paths">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.3em] text-amber">
              <span className="inline-block h-px w-10 bg-amber" aria-hidden="true" />
              Two ways to work with KP Strategy
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
            <Reveal className="h-full">
              <div data-testid="path-sprint" className="flex h-full flex-col border border-line bg-white p-8 sm:p-10">
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-amber">
                  Strategy Sprint
                </p>
                <h3 className="mt-4 font-serif text-2xl font-normal text-ink sm:text-3xl">
                  I need help figuring out what to do.
                </h3>
                <p className="mt-4 text-base leading-relaxed text-body">
                  For when the challenge isn&apos;t fully defined, priorities
                  are unclear, or you need an outside perspective to determine
                  the right path forward.
                </p>
                <p className="mt-6 border-t border-line pt-5 font-mono text-xs uppercase tracking-[0.15em] text-faint">
                  Focus: Diagnose → Clarify → Prioritize → Design → Activate
                </p>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.15em] text-faint">
                  Outcome: Clear direction + a practical Strategy &amp; Systems Blueprint
                </p>
                <div className="mt-auto pt-8">
                  <Link data-testid="path-sprint-cta" to="/contact?type=strategy-sprint" className={pillCls}>
                    Start with a Strategy Sprint
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="h-full">
              <div data-testid="path-workshop" className="flex h-full flex-col border border-line bg-white p-8 sm:p-10">
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-amber">
                  Workshop
                </p>
                <h3 className="mt-4 font-serif text-2xl font-normal text-ink sm:text-3xl">
                  I know the challenge and want my team to build capability.
                </h3>
                <p className="mt-4 text-base leading-relaxed text-body">
                  For when you have a specific challenge and want your team to
                  develop the skills, tools or practices to address it.
                </p>
                <p className="mt-6 border-t border-line pt-5 font-mono text-xs uppercase tracking-[0.15em] text-faint">
                  Focus: Learn → Practice → Apply → Build capability
                </p>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.15em] text-faint">
                  Outcome: Team capability + practical tools / practices
                </p>
                <div className="mt-auto pt-8">
                  <Link data-testid="path-workshop-cta" to="/contact?type=workshop" className={pillCls}>
                    Discuss a Workshop
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <p className="text-base text-body">
                Not sure which is right for you? Start with the free Strategy
                &amp; Systems Assessment.
              </p>
              <AssessmentButton testid="two-paths-assessment" dark={false} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Strategy Sprint */}
      <section id="strategy-sprint" className="scroll-mt-20 border-t border-line bg-parch py-20 sm:py-28" data-testid="sprint-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="01 — Strategy Sprint"
            title="From uncertainty to a practical path forward."
            copy="A focused consulting engagement to understand what's happening, identify what matters most and build a practical path forward."
            testid="sprint-head"
          />
          <Reveal delay={0.15}>
            <div
              data-testid="sprint-details"
              className="mt-14 grid grid-cols-1 gap-6 border-t border-ink/10 py-10 lg:grid-cols-12 lg:gap-10"
            >
              <div className="lg:col-span-4">
                <p className="font-mono text-[11px] text-amber">01</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                  Strategy
                </p>
                <h3 className="mt-2 font-serif text-2xl font-normal text-ink sm:text-3xl">
                  Strategy Sprint
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 lg:col-span-8">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                    What it addresses
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-body">
                    Turning uncertainty into a clear understanding of where to
                    focus and what to do next.
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                    Who it&apos;s for
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-body">
                    Founders, leaders and organizations facing a strategic,
                    operational, product or AI challenge — but unsure of the
                    right path forward.
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                    You leave with
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-body">
                    A practical Strategy &amp; Systems Blueprint.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-wrap items-center gap-7">
              <Link data-testid="sprint-cta" to="/contact?type=strategy-sprint" className={pillCls}>
                Start with a Strategy Sprint
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
              <span className="flex flex-wrap items-center gap-3 text-sm text-body">
                Prefer to talk first?
                <GhostLink testid="sprint-cta-book-call" href={CALENDLY_URL}>
                  Book an Intro Call
                </GhostLink>
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 flex items-baseline gap-3 font-mono text-xs uppercase tracking-[0.2em] text-faint">
              <span className="inline-block h-px w-8 bg-amber" aria-hidden="true" />
              The sprint may lead into one or more of our four service areas — depending on what your organization needs next.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Workshops */}
      <section id="the-workshops" className="scroll-mt-20 border-t border-line py-20 sm:py-28" data-testid="workshops-list-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="02 — The Workshops"
            title="Workshops built around the way your team actually works."
            copy="Interactive sessions designed around your organization's real challenges — not generic training. Every workshop is tailored to your team, your context and your goals."
            testid="workshops-list-head"
          />
          <Reveal delay={0.15}>
            <div className="mt-12 max-w-3xl">
              <p className="font-serif text-xl font-normal text-ink sm:text-2xl">
                Built around four ways of working better.
              </p>
              <p className="mt-3 text-base leading-relaxed text-body">
                Every workshop connects to one of KP Strategy&apos;s core
                service areas — so capability building can support the broader
                work you&apos;re already doing.
              </p>
              <div className="mt-6 flex flex-wrap gap-2.5" data-testid="workshops-area-links">
                {FOUR_AREAS.map((a) => (
                  <Link
                    key={a.number}
                    to="/services"
                    data-testid={`workshops-area-link-${a.number}`}
                    className="group border border-line bg-paper px-4 py-2.5 transition-colors duration-300 hover:border-amber/60"
                  >
                    <span className="font-mono text-[11px] uppercase tracking-wider text-ink">
                      <span className="text-amber">{a.number}</span> — {a.name}
                    </span>
                    <span className="ml-2 font-serif text-sm font-light italic text-body transition-colors group-hover:text-amber">
                      {a.tagline}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
          <div className="mt-14 border-t border-ink/10">
            {WORKSHOPS.map((w, i) => (
              <Reveal key={w.name} delay={i * 0.05}>
                <div
                  data-testid={w.testid}
                  className="grid grid-cols-1 gap-6 border-b border-ink/10 py-10 lg:grid-cols-12 lg:gap-10"
                >
                  <div className="lg:col-span-4">
                    <p className="font-mono text-[11px] text-amber">
                      {String(i + 1).padStart(2, "0")}
                    </p>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                      {w.area}
                    </p>
                    <h3 className="mt-2 font-serif text-2xl font-normal text-ink sm:text-3xl">
                      {w.name}
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 lg:col-span-8">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                        What it addresses
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-body">{w.addresses}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                        Who it&apos;s for
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-body">{w.who}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
                        You leave with
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-body">{w.leave}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Formats */}
      <section className="border-t border-line bg-parch py-16 sm:py-20" data-testid="workshops-formats">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-faint">
              03 — Formats
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
            {FORMATS.map((f, i) => (
              <Reveal key={f.name} delay={i * 0.08} className="h-full">
                <div
                  data-testid={f.testid}
                  className="flex h-full flex-col border border-line bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-amber/60 sm:p-10"
                >
                  <h3 className="font-serif text-2xl font-normal text-ink">
                    {f.name}
                  </h3>
                  <span className="mt-5 block h-px w-10 bg-amber transition-all duration-500 group-hover:w-full" />
                  <p className="mt-5 text-sm leading-relaxed text-body sm:text-base">
                    {f.note}
                  </p>
                  <ul className="mt-6 flex flex-wrap gap-2">
                    {f.options.map((o) => (
                      <li
                        key={o}
                        className="border border-line bg-paper px-3.5 py-2 font-mono text-[11px] uppercase tracking-wider text-body"
                      >
                        {o}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-8">
                    <Link
                      data-testid={`${f.testid}-cta`}
                      to={f.to}
                      className={pillCls}
                    >
                      {f.cta}
                      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-ink py-24 text-paper sm:py-32" data-testid="workshops-final-cta">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber">
              04 — Next Step
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl md:text-5xl">
              Get Your Free Strategy &amp; Systems Assessment.
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-paper/70 sm:text-lg">
              Identify where you&apos;re losing clarity, capacity or momentum —
              and what to focus on next.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-7">
              <AssessmentButton testid="workshops-final-cta-assessment" dark />
              <GhostLink
                testid="workshops-final-cta-book-call"
                href={CALENDLY_URL}
                dark
              >
                Book an Intro Call
              </GhostLink>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
